import React, { useState, useEffect } from 'react';
import { X, SlidersHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API } from '../App';
import { tf } from '../utils/translateFeature';
import { formatPlaceName } from '../utils/placeName';

const FilterPanel = ({ isOpen, onClose, onApplyFilters, currentFilters = {} }) => {
  const { t } = useTranslation();
  const [filterOptions, setFilterOptions] = useState({
    groupSizes: [],
    dietaryOptions: [],
    eventTypes: [],
    venueTypes: [],
    businessCategories: [],
    additionalFeatures: [],
    accessibilityFeatures: []
  });
  const [cityOptions, setCityOptions] = useState([]);
  
  const [selectedFilters, setSelectedFilters] = useState({
    groupSizes: [],
    dietaryOptions: [],
    eventTypes: [],
    venueType: [],
    businessCategories: [],
    additionalFeatures: [],
    accessibilityFeatures: [],
    openNow: false,
    openLaterToday: false,
    openWeekends: false,
    customEventType: '',
    customCategory: '',
    customFeature: '',
    city: '',
  });

  useEffect(() => {
    fetchFilterOptions();
    fetchCityOptions();
  }, []);

  useEffect(() => {
    if (currentFilters) {
      setSelectedFilters(prev => ({ ...prev, ...currentFilters }));
    }
  }, [currentFilters]);

  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${API}/filters`);
      setFilterOptions(response.data);
    } catch (error) {      console.error('Error fetching filter options:', error);
    }
  };

  const fetchCityOptions = async () => {
    try {
      const response = await axios.get(`${API}/businesses/cities`);
      const list = response.data?.cities || [];
      // Alphabetical sort for the dropdown (the endpoint's default order is
      // popularity, used by the "Cities nearby" pill row).
      list.sort((a, b) => (a.label || '').localeCompare(b.label || ''));
      setCityOptions(list);
    } catch (error) {
      // non-critical — filter just lacks the dropdown
      console.warn('Could not load city options:', error);
    }
  };

  const toggleFilter = (category, value) => {
    setSelectedFilters(prev => {
      const current = prev[category] || [];
      if (current.includes(value)) {
        return { ...prev, [category]: current.filter(v => v !== value) };
      } else {
        return { ...prev, [category]: [...current, value] };
      }
    });
  };

  const toggleBoolean = (key) => {
    setSelectedFilters(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const resetAll = () => {
    setSelectedFilters({
      groupSizes: [],
      dietaryOptions: [],
      eventTypes: [],
      venueType: [],
      businessCategories: [],
      additionalFeatures: [],
      accessibilityFeatures: [],
      openNow: false,
      openLaterToday: false,
      openWeekends: false,
      customEventType: '',
      customCategory: '',
      customFeature: '',
      city: '',
    });
  };

  const handleApply = () => {
    onApplyFilters(selectedFilters);
    onClose();
  };

  const isSelected = (category, value) => {
    return (selectedFilters[category] || []).includes(value);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white w-full max-w-lg rounded-t-3xl sm:rounded-3xl overflow-hidden flex flex-col" style={{ maxHeight: 'calc(100vh - 4rem)', maxHeight: 'calc(100dvh - 4rem)' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="h-5 w-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">{t('filters', 'Filters')}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            data-testid="close-filter-btn"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">

          {/* Location */}
          {cityOptions.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('location', 'Location')}</h3>
              <select
                value={selectedFilters.city || ''}
                onChange={(e) => setSelectedFilters((prev) => ({ ...prev, city: e.target.value }))}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                data-testid="filter-city-select"
              >
                <option value="">{t('allLocations', 'All locations')}</option>
                {cityOptions.map((c) => (
                  <option key={c.value} value={c.value}>
                    {formatPlaceName(c.label)} ({c.count})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Group Size */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('groupSize', 'Group Size')}</h3>
            <div className="flex flex-wrap gap-2">
              {filterOptions.groupSizes?.map((size) => (
                <button
                  key={size}
                  onClick={() => toggleFilter('groupSizes', size)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    isSelected('groupSizes', size)
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  data-testid={`filter-groupsize-${size.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                >
                  {tf(t, size)}
                </button>
              ))}
            </div>
          </div>

          {/* Dietary Options */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('dietaryOptions', 'Dietary Options')}</h3>
            <div className="flex flex-wrap gap-2">
              {filterOptions.dietaryOptions?.map((option) => (
                <button
                  key={option}
                  onClick={() => toggleFilter('dietaryOptions', option)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    isSelected('dietaryOptions', option)
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  data-testid={`filter-dietary-${option.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                >
                  {tf(t, option)}
                </button>
              ))}
            </div>
          </div>

          {/* Opening Hours */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('openingHours', 'Opening Hours')}</h3>
            <div className="space-y-3">
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm text-gray-700">{t('openNow', 'Open Now')}</span>
                <button
                  onClick={() => toggleBoolean('openNow')}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    selectedFilters.openNow ? 'bg-purple-600' : 'bg-gray-200'
                  }`}
                  data-testid="filter-open-now"
                >
                  <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${
                    selectedFilters.openNow ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </label>
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm text-gray-700">{t('openLaterToday', 'Open Later Today')}</span>
                <button
                  onClick={() => toggleBoolean('openLaterToday')}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    selectedFilters.openLaterToday ? 'bg-purple-600' : 'bg-gray-200'
                  }`}
                  data-testid="filter-open-later"
                >
                  <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${
                    selectedFilters.openLaterToday ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </label>
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm text-gray-700">{t('openWeekends', 'Open Weekends')}</span>
                <button
                  onClick={() => toggleBoolean('openWeekends')}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    selectedFilters.openWeekends ? 'bg-purple-600' : 'bg-gray-200'
                  }`}
                  data-testid="filter-open-weekends"
                >
                  <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${
                    selectedFilters.openWeekends ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </label>
            </div>
          </div>

          {/* Event Type */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('eventType', 'Event Type')}</h3>
            <div className="flex flex-wrap gap-2">
              {filterOptions.eventTypes?.map((type) => (
                <button
                  key={type}
                  onClick={() => toggleFilter('eventTypes', type)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    isSelected('eventTypes', type)
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  data-testid={`filter-event-${type.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                >
                  {tf(t, type)}
                </button>
              ))}
            </div>
            {isSelected('eventTypes', 'Other') && (
              <input
                type="text"
                value={selectedFilters.customEventType || ''}
                onChange={(e) => setSelectedFilters(prev => ({ ...prev, customEventType: e.target.value }))}
                placeholder={t('specifyEventType', 'Type event type to search...')}
                className="mt-2 w-full px-3 py-2 border border-purple-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none bg-purple-50"
                data-testid="filter-custom-event-type"
              />
            )}
          </div>

          {/* Venue Type */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('venueType', 'Venue Type')}</h3>
            <div className="flex flex-wrap gap-2">
              {filterOptions.venueTypes?.map((type) => (
                <button
                  key={type}
                  onClick={() => toggleFilter('venueType', type)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    isSelected('venueType', type)
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  data-testid={`filter-venue-${type.toLowerCase()}`}
                >
                  {tf(t, type)}
                </button>
              ))}
            </div>
          </div>

          {/* Business Categories */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('businessCategories', 'Business Categories')}</h3>
            <div className="flex flex-wrap gap-2">
              {filterOptions.businessCategories?.map((cat) => (
                <button
                  key={cat}
                  onClick={() => toggleFilter('businessCategories', cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    isSelected('businessCategories', cat)
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  data-testid={`filter-category-${cat.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                >
                  {cat}
                </button>
              ))}
            </div>
            {isSelected('businessCategories', 'Others') && (
              <input
                type="text"
                value={selectedFilters.customCategory || ''}
                onChange={(e) => setSelectedFilters(prev => ({ ...prev, customCategory: e.target.value }))}
                placeholder={t('specifyCategory', 'Type category to search...')}
                className="mt-2 w-full px-3 py-2 border border-purple-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none bg-purple-50"
                data-testid="filter-custom-category"
              />
            )}
          </div>

          {/* Additional Options */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('additionalOptions', 'Additional Options')}</h3>
            <div className="space-y-3">
              {filterOptions.additionalFeatures?.map((feature) => (
                <label key={feature} className="flex items-center justify-between cursor-pointer">
                  <span className="text-sm text-gray-700">{tf(t, feature)}</span>
                  <button
                    onClick={() => toggleFilter('additionalFeatures', feature)}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      isSelected('additionalFeatures', feature) ? 'bg-purple-600' : 'bg-gray-200'
                    }`}
                    data-testid={`filter-feature-${feature.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                  >
                    <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${
                      isSelected('additionalFeatures', feature) ? 'translate-x-6' : 'translate-x-0.5'
                    }`} />
                  </button>
                </label>
              ))}
              {isSelected('additionalFeatures', 'Other') && (
                <input
                  type="text"
                  value={selectedFilters.customFeature || ''}
                  onChange={(e) => setSelectedFilters(prev => ({ ...prev, customFeature: e.target.value }))}
                  placeholder={t('specifyFeature', 'Type feature to search...')}
                  className="w-full px-3 py-2 border border-purple-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none bg-purple-50"
                  data-testid="filter-custom-feature"
                />
              )}
            </div>
          </div>

          {/* Accessibility & Facilities */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('accessibilityFacilities', 'Accessibility & Facilities')}</h3>
            <div className="space-y-3">
              {filterOptions.accessibilityFeatures?.map((feature) => (
                <label key={feature} className="flex items-center justify-between cursor-pointer">
                  <span className="text-sm text-gray-700">{tf(t, feature)}</span>
                  <button
                    onClick={() => toggleFilter('accessibilityFeatures', feature)}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      isSelected('accessibilityFeatures', feature) ? 'bg-purple-600' : 'bg-gray-200'
                    }`}
                    data-testid={`filter-accessibility-${feature.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                  >
                    <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${
                      isSelected('accessibilityFeatures', feature) ? 'translate-x-6' : 'translate-x-0.5'
                    }`} />
                  </button>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Footer Actions - sticky at bottom, never hidden */}
        <div className="p-4 border-t bg-white flex gap-3 flex-shrink-0 sticky bottom-0" style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}>
          <button
            onClick={resetAll}
            className="flex-1 py-3 rounded-xl bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-colors"
            data-testid="reset-filters-btn"
          >
            {t('resetAll', 'Reset All')}
          </button>
          <button
            onClick={handleApply}
            className="flex-1 py-3 rounded-xl bg-purple-600 text-white font-medium hover:bg-purple-700 transition-colors"
            data-testid="apply-filters-btn"
          >
            {t('applyFilters', 'Apply Filters')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;
